'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { useRouter } from 'next/navigation'
import Image from 'next/image'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { X } from 'lucide-react'
import type { TeamMember } from '@/types/doctor'
import { getDisplayImage } from '@/types/doctor'
import ClinicianProfileContent from '@/components/team/ClinicianProfileContent'
import { useTrackClinicianProfileView } from '@/lib/analytics-hooks'
import type { FC } from 'react'

interface BioModalProps {
  member: TeamMember | null
  onClose: () => void
}

/**
 * BioModal - clinician profile modal.
 *
 * Mobile:  Sheet-style layout - short image header with content sheet
 *          overlapping from below with rounded top corners.
 * Desktop: Side-by-side with glassmorphic overlap panel.
 */
const BioModal: FC<BioModalProps> = ({ member, onClose }) => {
  const router = useRouter()
  // Analytics: one clinician_profile_view per opening; no identity is sent.
  useTrackClinicianProfileView(Boolean(member))
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const [scrollbarHeight, setScrollbarHeight] = useState(0)
  const [scrollbarTop, setScrollbarTop] = useState(0)
  const [isVisible, setIsVisible] = useState(false)
  const [translateY, setTranslateY] = useState(0)
  const [isOverscrolling, setIsOverscrolling] = useState(false)
  const [isBioExpanded, setIsBioExpanded] = useState(false)

  const animationFrameRef = useRef<number | null>(null)
  const velocityRef = useRef(0)
  const translateYRef = useRef(0)
  const isOverscrollingRef = useRef(false)
  const touchStartYRef = useRef(0)
  const lastTouchYRef = useRef(0)
  const isDraggingRef = useRef(false)
  const wheelTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const scrollUpdateFrameRef = useRef<number | null>(null)
  // The modal is controlled (no DialogTrigger), so Radix's default
  // close-autofocus has no trigger to return to. We capture the opener in
  // Radix's open lifecycle and restore it in its close lifecycle.
  const previouslyFocusedRef = useRef<HTMLElement | null>(null)

  // Reset bio expanded state when member changes
  useEffect(() => {
    setIsBioExpanded(false)
  }, [member])

  const updateScrollbar = useCallback((overscrollAmount = 0) => {
    const container = scrollContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const scrollableHeight = scrollHeight - clientHeight

    if (scrollableHeight <= 0) {
      setIsVisible(false)
      return
    }

    setIsVisible(true)

    const baseThumbHeight = Math.max(
      (clientHeight / scrollHeight) * clientHeight,
      40,
    )
    const maxThumbTop = clientHeight - baseThumbHeight

    const scrollRatio = scrollTop / scrollableHeight
    let thumbTop = scrollRatio * maxThumbTop
    let thumbHeight = baseThumbHeight

    if (overscrollAmount !== 0) {
      const maxScroll = scrollHeight - clientHeight
      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop >= maxScroll - 1

      if (isAtTop && overscrollAmount > 0) {
        const squeezeAmount = Math.min(
          Math.abs(overscrollAmount) * 0.3,
          baseThumbHeight * 0.6,
        )
        thumbHeight = baseThumbHeight - squeezeAmount
        thumbTop = 0
      } else if (isAtBottom && overscrollAmount < 0) {
        const squeezeAmount = Math.min(
          Math.abs(overscrollAmount) * 0.3,
          baseThumbHeight * 0.6,
        )
        thumbHeight = baseThumbHeight - squeezeAmount
        thumbTop = clientHeight - thumbHeight
      }
    }

    setScrollbarHeight(thumbHeight)
    setScrollbarTop(thumbTop)
  }, [])

  const scheduleScrollbarUpdate = useCallback(() => {
    if (scrollUpdateFrameRef.current) {
      cancelAnimationFrame(scrollUpdateFrameRef.current)
    }

    scrollUpdateFrameRef.current = requestAnimationFrame(() => {
      updateScrollbar(translateYRef.current)

      const container = scrollContainerRef.current
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container
        const maxScroll = scrollHeight - clientHeight
        const isAtBoundary = scrollTop <= 0 || scrollTop >= maxScroll - 1

        if (isOverscrollingRef.current || isAtBoundary) {
          scheduleScrollbarUpdate()
        }
      }
    })
  }, [updateScrollbar])

  const animateElastic = useCallback(() => {
    if (
      Math.abs(translateYRef.current) < 0.1 &&
      Math.abs(velocityRef.current) < 0.05
    ) {
      setTranslateY(0)
      setIsOverscrolling(false)
      updateScrollbar(0)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      return
    }

    const springStrength = 0.18
    const damping = 0.82

    velocityRef.current += -translateYRef.current * springStrength
    velocityRef.current *= damping

    const newTranslateY = translateYRef.current + velocityRef.current

    setTranslateY(newTranslateY)
    translateYRef.current = newTranslateY
    updateScrollbar(newTranslateY)
    animationFrameRef.current = requestAnimationFrame(animateElastic)
  }, [updateScrollbar])

  const startBounceBack = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
    }
    velocityRef.current = 0
    animationFrameRef.current = requestAnimationFrame(animateElastic)
  }, [animateElastic])

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      const container = scrollContainerRef.current
      if (!container) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const maxScroll = scrollHeight - clientHeight
      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop >= maxScroll - 1

      if ((isAtTop && e.deltaY < 0) || (isAtBottom && e.deltaY > 0)) {
        e.preventDefault()
        e.stopPropagation()

        const currentResistance =
          1 / (1 + Math.abs(translateYRef.current) * 0.008)
        const resistance = 0.5 * currentResistance

        const newTranslate = translateYRef.current - e.deltaY * resistance

        setTranslateY(newTranslate)
        translateYRef.current = newTranslate
        setIsOverscrolling(true)
        updateScrollbar(newTranslate)

        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }

        if (wheelTimeoutRef.current) {
          clearTimeout(wheelTimeoutRef.current)
        }

        wheelTimeoutRef.current = setTimeout(() => {
          startBounceBack()
        }, 150)
      } else {
        scheduleScrollbarUpdate()

        if (isOverscrollingRef.current) {
          setTranslateY(0)
          translateYRef.current = 0
          setIsOverscrolling(false)
          updateScrollbar(0)
          if (animationFrameRef.current) {
            cancelAnimationFrame(animationFrameRef.current)
            animationFrameRef.current = null
          }
        }
      }
    },
    [updateScrollbar, startBounceBack, scheduleScrollbarUpdate],
  )

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartYRef.current = e.touches[0].clientY
    lastTouchYRef.current = e.touches[0].clientY
    isDraggingRef.current = true

    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }
  }, [])

  const handleTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isDraggingRef.current) return

      const container = scrollContainerRef.current
      if (!container) return

      const { scrollTop, scrollHeight, clientHeight } = container
      const maxScroll = scrollHeight - clientHeight
      const isAtTop = scrollTop <= 0
      const isAtBottom = scrollTop >= maxScroll - 1

      const currentY = e.touches[0].clientY
      const deltaY = currentY - lastTouchYRef.current
      lastTouchYRef.current = currentY

      if ((isAtTop && deltaY > 0) || (isAtBottom && deltaY < 0)) {
        e.preventDefault()

        const currentResistance =
          1 / (1 + Math.abs(translateYRef.current) * 0.006)
        const resistance = 0.6 * currentResistance

        const newTranslate = translateYRef.current + deltaY * resistance

        setTranslateY(newTranslate)
        translateYRef.current = newTranslate
        setIsOverscrolling(true)
        updateScrollbar(newTranslate)
      } else {
        scheduleScrollbarUpdate()

        if (
          isOverscrollingRef.current &&
          ((isAtTop && deltaY < 0) || (isAtBottom && deltaY > 0))
        ) {
          setTranslateY(0)
          translateYRef.current = 0
          setIsOverscrolling(false)
          updateScrollbar(0)
        }
      }
    },
    [updateScrollbar, scheduleScrollbarUpdate],
  )

  const handleTouchEnd = useCallback(() => {
    isDraggingRef.current = false

    if (Math.abs(translateYRef.current) > 0) {
      startBounceBack()
    }
  }, [startBounceBack])

  const handleScroll = useCallback(() => {
    scheduleScrollbarUpdate()

    const container = scrollContainerRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const maxScroll = scrollHeight - clientHeight
    const isAtTop = scrollTop <= 0
    const isAtBottom = scrollTop >= maxScroll - 1

    if (
      !isAtTop &&
      !isAtBottom &&
      isOverscrollingRef.current &&
      !isDraggingRef.current
    ) {
      setTranslateY(0)
      translateYRef.current = 0
      setIsOverscrolling(false)
      updateScrollbar(0)
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [updateScrollbar, scheduleScrollbarUpdate])

  const handleBookAppointment = useCallback(() => {
    onClose()
    router.push('/book')
  }, [router, onClose])

  // Sync refs with state
  useEffect(() => {
    translateYRef.current = translateY
  }, [translateY])

  useEffect(() => {
    isOverscrollingRef.current = isOverscrolling
  }, [isOverscrolling])

  // Initialize scrollbar
  useEffect(() => {
    if (!member) return

    const container = scrollContainerRef.current
    if (!container) return

    const timer = setTimeout(() => updateScrollbar(0), 100)

    const resizeObserver = new ResizeObserver(() => updateScrollbar(0))
    resizeObserver.observe(container)

    return () => {
      clearTimeout(timer)
      resizeObserver.disconnect()
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
      }
      if (wheelTimeoutRef.current) {
        clearTimeout(wheelTimeoutRef.current)
      }
      if (scrollUpdateFrameRef.current) {
        cancelAnimationFrame(scrollUpdateFrameRef.current)
      }
    }
  }, [member, updateScrollbar])

  /* ───── Handle "no member" AFTER all hooks ───── */
  if (!member) {
    return <Dialog open={false} onOpenChange={onClose} />
  }

  /* ───── Derived data ───── */
  const displayImage = getDisplayImage(member, true)

  /* ───── Shared scroll container props ───── */
  const scrollProps = {
    onScroll: handleScroll,
    onWheel: handleWheel,
    onTouchStart: handleTouchStart,
    onTouchMove: handleTouchMove,
    onTouchEnd: handleTouchEnd,
  }

  /* ───── Custom scrollbar renderer ───── */
  const renderScrollbar = (size: 'sm' | 'lg') =>
    isVisible ? (
      <div
        className={`absolute top-0 bottom-0 pointer-events-none ${
          size === 'sm' ? 'right-1 w-1' : 'right-3 w-2'
        }`}
        style={{ zIndex: 50 }}
      >
        <div
          className={`absolute right-0 rounded-full ${
            size === 'sm' ? 'w-1' : 'w-2'
          }`}
          style={{
            height: `${scrollbarHeight}px`,
            top: `${scrollbarTop}px`,
            background: 'hsl(187, 85%, 66%)',
            transition: isOverscrolling ? 'none' : 'opacity 0.2s ease',
            opacity: isOverscrolling ? 0.7 : 1,
          }}
        />
      </div>
    ) : null

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent
        className="max-w-7xl w-[97vw] sm:w-[95vw] h-[94vh] sm:h-[90vh] p-0 overflow-hidden bg-transparent border-none shadow-2xl rounded-2xl [&>button]:hidden"
        onPointerDownOutside={onClose}
        onOpenAutoFocus={() => {
          // Runs before Radix moves focus into the dialog, so activeElement
          // is still the card/button that opened the modal.
          previouslyFocusedRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null
        }}
        onCloseAutoFocus={(event) => {
          event.preventDefault()
          previouslyFocusedRef.current?.focus()
          previouslyFocusedRef.current = null
        }}
      >
        <DialogTitle className="sr-only">
          {member.name} – {member.title}
        </DialogTitle>

        {/* Background container */}
        <div
          className="relative w-full h-full rounded-2xl overflow-hidden"
          style={{
            background:
              'radial-gradient(132.8% 123.47% at -6.67% -7.03%, hsl(235, 18%, 12%) 0%, hsl(234, 18%, 38%) 51.7%, hsl(180, 7%, 3%) 98.56%)',
          }}
        >
          {/* Close button - desktop only (mobile one lives inside the sheet) */}
          <button
            onClick={onClose}
            className="hidden md:flex absolute top-4 right-6 lg:right-8 z-50 p-2 rounded-full bg-white/10 hover:bg-white/20 active:bg-white/30 backdrop-blur-sm transition-colors"
            aria-label="Close modal"
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* ═══════ MOBILE LAYOUT (< md) ═══════ */}
          <div className="md:hidden relative w-full h-full">
            {/* Mobile close button - floats above everything */}
            <button
              onClick={onClose}
              className="absolute top-[clamp(150px,26vh,240px)] right-3 z-[60] p-3 -translate-y-1/2 rounded-full bg-[hsl(222,47%,15%)] border border-white/15 shadow-lg shadow-black/40 active:scale-95 transition-transform touch-manipulation"
              aria-label="Close modal"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="w-5 h-5 text-white" />
            </button>
            {/* Image - compact header */}
            <div className="relative w-full h-[32vh] min-h-[180px] max-h-[260px]">
              <Image
                src={displayImage}
                alt={`${member.name}, ${member.title}`}
                fill
                className="object-cover object-top"
                sizes="100vw"
                priority
              />
              {/* Gradient that blends image into the dark sheet */}
              <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-[hsl(230,20%,10%)] via-[hsl(230,20%,10%)]/70 to-transparent" />
            </div>

            {/* Content sheet - overlaps image from below */}
            <div className="absolute inset-x-0 bottom-0 top-[clamp(150px,26vh,240px)] z-10">
              <div className="relative h-full bg-[hsl(222,47%,11%)]/75 backdrop-blur-2xl border-t border-white/[0.08] rounded-t-[20px] shadow-[0_-4px_24px_rgba(0,0,0,0.5)] overflow-hidden">
                {/* Drag indicator pill */}
                <div className="flex justify-center pt-3 pb-1.5">
                  <div className="w-9 h-[3px] rounded-full bg-white/20" />
                </div>

                {/* Scrollable area */}
                <div
                  ref={scrollContainerRef}
                  {...scrollProps}
                  className="h-[calc(100%-18px)] overflow-y-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                  <div
                    ref={contentRef}
                    className="pr-2"
                    style={{
                      transform: isOverscrolling
                        ? `translateY(${translateY}px)`
                        : 'translateY(0)',
                      willChange: isOverscrolling ? 'transform' : 'auto',
                    }}
                  >
                    <div className="px-5 pb-8 pt-1.5">
                      <ClinicianProfileContent
                        member={member}
                        size="sm"
                        isBioExpanded={isBioExpanded}
                        onToggleBio={() => setIsBioExpanded((prev) => !prev)}
                        onBookAppointment={handleBookAppointment}
                      />
                    </div>
                  </div>
                </div>

                {renderScrollbar('sm')}
              </div>
            </div>
          </div>

          {/* ═══════ DESKTOP LAYOUT (≥ md) ═══════ */}
          <div className="hidden md:flex relative w-full h-full flex-row">
            {/* Left - Image */}
            <div className="relative w-[46%] lg:w-[50%] h-full flex-shrink-0">
              <Image
                src={displayImage}
                alt={`${member.name}, ${member.title}`}
                fill
                className="object-cover object-top scale-90 origin-bottom-left"
                sizes="50vw"
                priority
              />
            </div>

            {/* Right - Glassmorphic scrollable panel */}
            <div className="absolute top-0 right-0 w-[68%] lg:w-[65%] h-full z-10">
              <div className="relative h-full bg-[hsl(222,47%,11%)]/40 backdrop-blur-xl border-l border-white/10 rounded-l-3xl rounded-tr-2xl shadow-2xl overflow-hidden">
                <div
                  ref={scrollContainerRef}
                  {...scrollProps}
                  className="h-full overflow-y-scroll [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden"
                >
                  <div
                    ref={contentRef}
                    className="pr-4"
                    style={{
                      transform: isOverscrolling
                        ? `translateY(${translateY}px)`
                        : 'translateY(0)',
                      willChange: isOverscrolling ? 'transform' : 'auto',
                    }}
                  >
                    <div className="px-8 py-10 lg:px-12 lg:py-12">
                      <ClinicianProfileContent
                        member={member}
                        size="lg"
                        isBioExpanded={isBioExpanded}
                        onToggleBio={() => setIsBioExpanded((prev) => !prev)}
                        onBookAppointment={handleBookAppointment}
                      />
                    </div>
                  </div>
                </div>

                {/* Inner highlight */}
                <div className="pointer-events-none absolute inset-0 rounded-l-3xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]" />

                {renderScrollbar('lg')}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default BioModal
